import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import './FeedCard.scss';

import ShopmyImage from '../General/ShopmyImage';
import { getPrettyNumber, getPrettyPriceDisplay } from '../../helpers/formatting';

const FeedCard = props => {
  const { card, rank } = props;
  const lines = buildCardLines(card) || {};
  const attribution = lines.attribution?.filter(Boolean).join(' · ');
  const meta = lines.meta?.filter(Boolean).join(' · ');
  return (
    <div className={cn('blend-feed-card', card.type)} title={buildDebugTooltip(card)}>
      <div className='card-image'>
        <CardImage card={card} />
        <span className='debug-chip'>
          #{rank} · {card.type}
        </span>
      </div>
      <div className='card-body'>
        {attribution && <div className='card-attribution'>{attribution}</div>}
        <div className='card-title'>{lines.title || 'Untitled'}</div>
        {meta && <div className='card-meta'>{meta}</div>}
      </div>
    </div>
  );
};

// Collections render as the app's mosaic of preview pins; products as a single shot.
const CardImage = ({ card }) => {
  const { type, data } = card;
  if (type === 'collection') {
    const pinImages = (data.preview_pins || []).map(pin => pin.image).filter(Boolean).slice(0, 4); // prettier-ignore
    if (pinImages.length) {
      return (
        <div className={`card-mosaic count-${pinImages.length}`}>
          {pinImages.map((image, index) => (
            <ShopmyImage key={index} source={image} alt={data.name} />
          ))}
        </div>
      );
    }
  }
  const image = type === 'product' ? data.images?.[0]?.image || data.image : data.image;
  return image ? <ShopmyImage source={image} alt={data.title || data.name} /> : <div className='card-image-placeholder' />;
};

// Attribution above the title, meta below — mirroring the app's feed tiles.
const buildCardLines = card => {
  const { data } = card;
  return {
    product: {
      title: data.title,
      attribution: [data.AllBrand_name, getPrettyPriceDisplay(data.fallbackPrice)],
      meta: [data.num_promoters && `${getPrettyNumber(data.num_promoters)} curators`, data.monthlyClicks && `${getPrettyNumber(data.monthlyClicks)} clicks/mo`]
    },
    collection: {
      title: data.name,
      attribution: [`by ${data.User_name || data.User_username}`],
      meta: [data.total_num_pins && `${data.total_num_pins} products`, data.lastUpdated && `updated ${moment(data.lastUpdated).fromNow()}`]
    }
  }[card.type];
};

const buildDebugTooltip = card => {
  const { data } = card;
  const stats =
    card.type === 'product'
      ? { blendScore: card.blendScore, promoters: data.num_promoters, weeklyClicks: data.weeklyClicks, monthlyClicks: data.monthlyClicks }
      : { blendScore: card.blendScore, products: data.total_num_pins, lastUpdated: data.lastUpdated };
  return Object.entries(stats)
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

FeedCard.propTypes = {
  card: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired
};

export default FeedCard;
